/**
 * ModelsSection Component
 *
 * Compact model configuration with expandable parameters:
 * - Table-like rows for quick scanning
 * - Click chevron to expand/collapse parameters
 * - Minimal visual noise
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Settings2, MessageSquare, Eye, Brain, Search, Sparkles, Palette, RotateCcw, Lock, type LucideIcon } from 'lucide-react';
import { ModelSelectorField } from '@/components/fields/ModelSelectorField';
import { ModelParametersPanel, type LinkedParameter } from '@/components/ModelSelector';
import { MERGED_FIELD_CONFIG as FIELD_CONFIG, FIELD_CONFIG as RAW_FIELD_CONFIG, getConfigValue } from '@/config/fieldConfig';
import { useI18n } from '@/i18n';
import { useModelDefaultsConfig } from '@/features/system-settings/hooks/useSystemConfig';
import type { ModelDefaultsConfig } from '@/types/system';

// Category styling
const CATEGORY_STYLES: Record<string, { icon: LucideIcon; color: string; bg: string; border: string }> = {
  '对话': { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-l-blue-400' },
  '视觉': { icon: Eye, color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-l-purple-400' },
  '处理': { icon: Brain, color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-l-rose-400' },
  '检索': { icon: Search, color: 'text-teal-500', bg: 'bg-teal-50', border: 'border-l-teal-400' },
  '画图': { icon: Palette, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-l-emerald-400' },
  '其他': { icon: Sparkles, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-l-amber-400' },
};

interface ModelsSectionProps {
  onChange: (name: string, value: any) => void;
  appContext: any;
  /** Section to highlight (scroll-to + flash animation) */
  highlightSection?: string | null;
}

// Global models (managed by system settings, read-only in agent config)
const GLOBAL_MODEL_ROWS = [
  { fieldName: 'models.memory.model_id', label: '记忆提取', category: '处理', hint: '摘要总结' },
  { fieldName: 'models.embedding.model_id', label: '向量嵌入', category: '检索', hint: '知识库' },
];

// Sentinel: fallbackHint set to this value means "uses system default, don't show arrow hint"
const FALLBACK_SYSTEM_DEFAULT = 'system_default' as const;

// Per-agent model definitions with optional fallback hints
// hookGate: when set, the row is only visible if this config path is truthy
const MODEL_ROWS = [
  // Conversation
  { fieldName: 'models.chat.model_id', label: '主对话', category: '对话', hint: '复杂分析' },
  { fieldName: 'models.chat.quick_model_id', label: '快速对话', category: '对话', hint: '简单问答', fallbackHint: FALLBACK_SYSTEM_DEFAULT, hookGate: 'hooks.quick_chat.enabled' },
  // Vision — gated by hooks.vision.enabled; quick vision also needs quick_chat
  { fieldName: 'models.vision.model_id', label: '视觉分析', category: '视觉', hint: '主模型不支持视觉时启用', hookGate: 'hooks.vision.enabled' },
  { fieldName: 'models.vision.quick_model_id', label: '快速视觉', category: '视觉', hint: '快速模型不支持视觉时启用', fallbackHint: FALLBACK_SYSTEM_DEFAULT, hookGate: ['hooks.vision.enabled', 'hooks.quick_chat.enabled'] as string[] },
  // Intelligence (per-agent only)
  { fieldName: 'models.classification.model_id', label: '意图分类', category: '处理', hint: '路由判断' },
  { fieldName: 'models.task.model_id', label: '子任务', category: '处理', hint: '压缩等', fallbackHint: FALLBACK_SYSTEM_DEFAULT },
  // Speech — gated by hooks.stt.enabled
  { fieldName: 'models.stt.model_id', label: '语音识别', category: '其他', hint: 'STT', hookGate: 'hooks.stt.enabled' },
  // Reasoning — gated by hooks.reasoning.enabled
  { fieldName: 'models.reasoning.model_id', label: '推理增强', category: '其他', hint: '思考链', hookGate: 'hooks.reasoning.enabled' },
  // Image generation
  { fieldName: 'models.image.model_id', label: '默认画图', category: '画图', hint: '快速画图' },
  { fieldName: 'models.image.quality_model_id', label: '高质量画图', category: '画图', hint: '精细画图' },
];

// Image generation config (static select fields, not model selectors)
// Model fields moved to MODEL_ROWS above — only non-model settings remain here
const IMAGE_CONFIG_FIELDS = [
  { fieldName: 'models.image.image_size', label: '默认尺寸', hint: '分辨率' },
  { fieldName: 'models.image.default_aspect_ratio', label: '默认比例', hint: '宽高比' },
];

/**
 * Get the default model value for a specific field from ModelDefaultsConfig
 */
function getDefaultForField(fieldName: string, defaults: ModelDefaultsConfig | undefined): string | null {
  if (!defaults) return null;

  // Map fieldName to ModelDefaultsConfig path (per-agent models only)
  const mapping: Record<string, string | null> = {
    'models.chat.model_id': defaults.chat?.model_id || null,
    'models.chat.quick_model_id': defaults.chat?.quick_model_id || null,
    'models.vision.model_id': defaults.vision?.model_id || null,
    'models.vision.quick_model_id': defaults.vision?.quick_model_id || null,
    'models.classification.model_id': defaults.classification?.model_id || null,
    'models.task.model_id': defaults.task?.model_id || null,
    'models.stt.model_id': defaults.stt?.model_id || null,
    'models.reasoning.model_id': defaults.reasoning?.model_id || null,
    'models.image.model_id': defaults.image?.model_id || null,
    'models.image.quality_model_id': defaults.image?.quality_model_id || null,
  };

  return mapping[fieldName] ?? null;
}

interface ModelRowProps {
  fieldName: string;
  label: string;
  hint: string;
  fallbackHint?: string;
  category: string;
  onChange: (name: string, value: any) => void;
  appContext: any;
  isExpanded: boolean;
  onToggleExpand: () => void;
  defaultValue: string | null;
}

/**
 * Build LinkedParameter objects from field config
 */
function buildLinkedParameters(
  linkedParamNames: string[] | undefined,
  formData: Record<string, unknown>,
  getFieldTranslation: (name: string) => { label?: string; options?: Record<string, string> } | undefined
): LinkedParameter[] {
  if (!linkedParamNames) return [];

  return linkedParamNames
    .map((paramName) => {
      const paramSchema = RAW_FIELD_CONFIG[paramName];
      if (!paramSchema) return null;

      const translation = getFieldTranslation(paramName);
      const paramLabel = translation?.label ||
        (typeof paramSchema.label === 'function' ? paramSchema.label() : paramSchema.label);

      const currentValue = getConfigValue(formData, paramName);

      let paramType: 'number' | 'select' | 'button_group' = 'number';
      if (paramSchema.type === 'select') {
        paramType = paramSchema.buttonGroup ? 'button_group' : 'select';
      }

      let options: Array<{ value: string; label: string }> | undefined;
      if (paramSchema.options) {
        options = paramSchema.options.map((opt) => ({
          value: opt.value,
          label: translation?.options?.[opt.value] || opt.label,
        }));
      }

      return {
        name: paramName,
        label: paramLabel,
        type: paramType,
        value: currentValue,
        options,
        min: paramSchema.min,
        max: paramSchema.max,
        step: paramSchema.step,
      } as LinkedParameter;
    })
    .filter((p): p is LinkedParameter => p !== null);
}

const ModelRow: React.FC<ModelRowProps> = ({
  fieldName,
  label,
  hint,
  fallbackHint,
  category,
  onChange,
  appContext,
  isExpanded,
  onToggleExpand,
  defaultValue,
}) => {
  const { getFieldTranslation, language } = useI18n();
  const rawFieldConfig = FIELD_CONFIG[fieldName];

  // Override emptyLabel to show the actual system default model name
  const fieldConfig = useMemo(() => {
    if (!rawFieldConfig || !defaultValue || !rawFieldConfig.allowEmpty) return rawFieldConfig;
    const label = language === 'zh'
      ? `使用系统默认 (${defaultValue})`
      : `Use system default (${defaultValue})`;
    return {
      ...rawFieldConfig,
      emptyLabel: label,
    };
  }, [rawFieldConfig, defaultValue, language]);

  const value = getConfigValue(appContext, fieldName) as string | null;
  const hasLinkedParams = (fieldConfig?.linkedParameters && fieldConfig.linkedParameters.length > 0) ?? false;
  const categoryStyle = CATEGORY_STYLES[category] || CATEGORY_STYLES['其他'];

  // Check if current value differs from default
  const canApplyDefault = defaultValue && value !== defaultValue;

  // Build linked parameters for the panel
  const linkedParams = useMemo(() => {
    if (!fieldConfig || !hasLinkedParams) return [];
    return buildLinkedParameters(fieldConfig.linkedParameters, appContext, getFieldTranslation);
  }, [fieldConfig, appContext, getFieldTranslation, hasLinkedParams]);

  if (!fieldConfig) return null;

  const handleValueChange = (newValue: string | null) => {
    onChange(fieldName, newValue);
  };

  const handleFormDataChange = (path: string, paramValue: unknown) => {
    onChange(path, paramValue);
  };

  const handleApplyDefault = () => {
    if (defaultValue) {
      onChange(fieldName, defaultValue);
    }
  };

  return (
    <div className={`border-b border-slate-100 last:border-b-0 border-l-2 ${categoryStyle.border}`}>
      {/* Main row */}
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50/50 transition-colors">
        {/* Apply default button */}
        <button
          type="button"
          onClick={canApplyDefault ? handleApplyDefault : undefined}
          disabled={!canApplyDefault}
          className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
            canApplyDefault
              ? 'text-blue-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer'
              : 'text-slate-200 cursor-default'
          }`}
          title={canApplyDefault ? `应用首选: ${defaultValue}` : (defaultValue ? '已是首选模型' : '无首选配置')}
        >
          <RotateCcw className="w-3 h-3" />
        </button>

        {/* Expand toggle (only if has params) */}
        <button
          type="button"
          onClick={hasLinkedParams ? onToggleExpand : undefined}
          className={`w-5 h-5 flex items-center justify-center rounded ${
            hasLinkedParams
              ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              : 'text-transparent cursor-default'
          }`}
        >
          {hasLinkedParams && (
            isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Label */}
        <div className="w-20 shrink-0">
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>

        {/* Hint */}
        <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{hint}</span>

        {/* Model Selector */}
        <div className="flex-1 min-w-0">
          <ModelSelectorField
            name={fieldName}
            schema={fieldConfig}
            value={value}
            onChange={handleValueChange}
            appContext={appContext}
            formData={appContext}
            onFormDataChange={handleFormDataChange}
            compact
          />
        </div>

        {/* Fallback hint */}
        {fallbackHint && fallbackHint !== FALLBACK_SYSTEM_DEFAULT && !value && (
          <span className="text-[10px] text-slate-400 shrink-0 hidden md:block">
            → {fallbackHint}
          </span>
        )}
      </div>

      {/* Expanded parameters - only parameters, no model selector */}
      {isExpanded && hasLinkedParams && linkedParams.length > 0 && (
        <div className="pl-10 pr-4 pb-2 bg-slate-50/50">
          <ModelParametersPanel
            parameters={linkedParams}
            onParameterChange={handleFormDataChange}
            compact
          />
        </div>
      )}
    </div>
  );
};

/**
 * Compact select row for image config fields (static options, not model selectors)
 */
const ImageSelectRow: React.FC<{
  fieldName: string;
  label: string;
  hint: string;
  onChange: (name: string, value: any) => void;
  appContext: any;
}> = ({ fieldName, label, hint, onChange, appContext }) => {
  const fieldConfig = FIELD_CONFIG[fieldName];
  if (!fieldConfig || !fieldConfig.options) return null;

  const value = getConfigValue(appContext, fieldName) ?? fieldConfig.default ?? '';
  const categoryStyle = CATEGORY_STYLES['画图'];

  return (
    <div className={`border-b border-slate-100 last:border-b-0 border-l-2 ${categoryStyle.border}`}>
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50/50 transition-colors">
        {/* Spacer to align with model rows (apply default + expand buttons) */}
        <div className="w-5 shrink-0" />
        <div className="w-5 shrink-0" />

        {/* Label */}
        <div className="w-20 shrink-0">
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>

        {/* Hint */}
        <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{hint}</span>

        {/* Select dropdown */}
        <div className="flex-1 min-w-0">
          <select
            value={String(value)}
            onChange={(e) => onChange(fieldName, e.target.value || null)}
            className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
          >
            {fieldConfig.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export const ModelsSection: React.FC<ModelsSectionProps> = ({
  onChange,
  appContext,
  highlightSection = null,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { data: modelDefaults } = useModelDefaultsConfig();
  const sectionRef = useRef<HTMLDivElement>(null);

  // Any linkedSection starting with 'models' targets this component
  const isHighlighted = Boolean(highlightSection?.startsWith('models'));

  // Scroll into view when highlighted
  useEffect(() => {
    if (isHighlighted && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [isHighlighted]);

  const toggleExpand = (fieldName: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  // Filter rows by hookGate visibility (supports single string or string[] AND logic)
  const visibleRows = MODEL_ROWS.filter((row) => {
    if (!row.hookGate) return true;
    const gates = Array.isArray(row.hookGate) ? row.hookGate : [row.hookGate];
    return gates.every((gate) => Boolean(getConfigValue(appContext, gate)));
  });

  const groupedRows = visibleRows.reduce((acc, row) => {
    if (!acc[row.category]) acc[row.category] = [];
    acc[row.category].push(row);
    return acc;
  }, {} as Record<string, typeof MODEL_ROWS>);

  return (
    <div
      ref={sectionRef}
      className={`bg-white rounded-lg border shadow-sm transition-all duration-300 ${
        isHighlighted ? 'border-blue-400 ring-2 ring-blue-200 animate-pulse' : 'border-slate-200'
      }`}
    >
      {/* Header */}
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-slate-100 ${isHighlighted ? 'bg-blue-50' : ''}`}>
        <Settings2 className="w-4 h-4 text-slate-400" />
        <h3 className={`text-sm font-medium ${isHighlighted ? 'text-blue-700' : 'text-slate-700'}`}>模型配置</h3>
        <span className="text-xs text-slate-400">· {visibleRows.length + IMAGE_CONFIG_FIELDS.length + GLOBAL_MODEL_ROWS.length} 项</span>
      </div>

      {/* Global Models (read-only, managed by system settings) */}
      <div className="border-b border-slate-200 bg-slate-50/80">
        <div className="px-4 py-1.5 flex items-center gap-2">
          <Lock className="w-3 h-3 text-amber-500" />
          <span className="text-[11px] font-medium text-amber-600">全局模型</span>
          <span className="text-[10px] text-slate-400">· 由系统设置管理</span>
        </div>
        {GLOBAL_MODEL_ROWS.map((row) => {
          const value = modelDefaults
            ? (modelDefaults as any)[row.fieldName.split('.')[1]]?.model_id
            : null;
          return (
            <div key={row.fieldName} className="flex items-center gap-2 py-1.5 px-3 border-b border-slate-100 last:border-b-0 opacity-60">
              <Lock className="w-3 h-3 text-slate-300 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="w-20 shrink-0">
                <span className="text-sm text-slate-500">{row.label}</span>
              </div>
              <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{row.hint}</span>
              <div className="flex-1 min-w-0">
                <span className="text-sm text-slate-400 truncate block">{value || '未配置'}</span>
              </div>
            </div>
          );
        })}
        {/* Reranker status — system-level toggle */}
        {(() => {
          const rerankEnabled = modelDefaults?.reranking?.enabled !== false;
          const rerankModel = modelDefaults?.reranking?.model_id;
          return (
            <div className="flex items-center gap-2 py-1.5 px-3 border-b border-slate-100 last:border-b-0 opacity-60">
              <Lock className="w-3 h-3 text-slate-300 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="w-20 shrink-0">
                <span className="text-sm text-slate-500">重排序</span>
              </div>
              <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">相关性</span>
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${rerankEnabled ? 'bg-green-400' : 'bg-slate-300'}`} />
                <span className="text-sm text-slate-400 truncate">
                  {rerankEnabled ? (rerankModel || '未配置模型') : '已关闭'}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Per-Agent Model Rows */}
      <div>
        {Object.entries(groupedRows).map(([category, rows]) => {
          const style = CATEGORY_STYLES[category] || CATEGORY_STYLES['其他'];
          const Icon = style.icon;
          return (
            <div key={category}>
              {/* Category label */}
              <div className={`px-4 py-1.5 ${style.bg} border-b border-slate-100 flex items-center gap-2`}>
                <Icon className={`w-3.5 h-3.5 ${style.color}`} />
                <span className={`text-[11px] font-medium ${style.color}`}>
                  {category}
                </span>
              </div>
              {/* Rows */}
              {rows.map((row) => (
                <ModelRow
                  key={row.fieldName}
                  {...row}
                  onChange={onChange}
                  appContext={appContext}
                  isExpanded={expandedRows.has(row.fieldName)}
                  onToggleExpand={() => toggleExpand(row.fieldName)}
                  defaultValue={getDefaultForField(row.fieldName, modelDefaults)}
                />
              ))}
              {/* Image non-model settings rendered inside 画图 category */}
              {category === '画图' && IMAGE_CONFIG_FIELDS.map((field) => (
                <ImageSelectRow
                  key={field.fieldName}
                  {...field}
                  onChange={onChange}
                  appContext={appContext}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModelsSection;
